import {
    ICUActivity,
    ICUBadge,
    ICUBadgeProgress,
    ICUInbox,
    ICUInstance,
    ICUProfile,
    ICUConfig,
    ICULevel,
    ICUAsset,
    ICUCurrencies,
    ICUTournament,
    ICULeaderboard,
} from './interfaces';

export default class CaptainUpAPI {
    private captain: ICUInstance;

    private initCaptain = (config: ICUConfig): Promise<ICUInstance> =>
        new Promise((resolve, reject) => {
            this.captain
                .up(config)
                .then(() => resolve(this.captain))
                .catch(reject);
        });

    public setupSDK(sdkUrl: string, config: ICUConfig): Promise<ICUInstance> {
        if (window.captain) {
            return this.initCaptain({ ...config, restart: true, recursive: true });
        }

        return new Promise((resolve, reject) => {
            const cuBundle = document.createElement('script');
            cuBundle.src = sdkUrl;
            cuBundle.onload = () => {
                this.captain = window.captain;

                return this.initCaptain(config).then(resolve).catch(reject);
            };

            cuBundle.onerror = reject;

            document.head.appendChild(cuBundle);
        });
    }

    // HTTPS request
    public getUser(): Promise<ICUProfile> {
        return this.captain.user.get();
    }

    // Takes data form `window.captain`
    public getBadges(): Promise<[ICUBadge[], Record<string, ICUBadgeProgress>]> {
        return this.captain
            .isReady()
            .then(() =>
                Promise.all([this.captain.badges.get(), this.captain.badges.getProgress()]),
            );
    }

    // HTTPS request
    // remove inbox from unread list and returns unread inboxes
    public markAsRead(id: string): Promise<{ data: ICUInbox[] }> {
        return this.captain.isReady().then(() => this.captain.inbox.markAsRead(id));
    }

    public getLevel(): Promise<ICULevel> {
        return this.captain.isReady().then(() => this.captain.levels.get());
    }

    public registerToTournament(tournamentId: string): Promise<boolean> {
        return this.captain.isReady().then(() => this.captain.tournaments.register(tournamentId));
    }

    public unregisterFromTournament(tournamentId: string): Promise<boolean> {
        return this.captain.isReady().then(() => this.captain.tournaments.unregister(tournamentId));
    }

    public getNextLevel(): Promise<ICULevel> {
        return this.captain.isReady().then(() => this.captain.levels.getNext());
    }

    public subscribeToLevels(listener: ICallback<ICULevel>): Promise<void> {
        return this.captain.isReady().then(() => this.captain.levels.state.subscribe(listener));
    }

    public subscribeToInbox(listener: ICallback<ICUInbox[]>): Promise<void> {
        return this.captain.isReady().then(() => this.captain.inbox.state.subscribe(listener));
    }

    public subscribeToActivities(listener: ICallback<ICUActivity[]>): Promise<void> {
        return this.captain.isReady().then(() => this.captain.activities.state.subscribe(listener));
    }

    public subscribeToCurrencies(listener: ICallback<ICUCurrencies>): Promise<void> {
        return this.captain.isReady().then(() => this.captain.currencies.state.subscribe(listener));
    }

    public getAssets(): Promise<ICUAsset[]> {
        return this.captain.isReady().then(() => this.captain.assets.get());
    }

    public getTournaments(): Promise<ICUTournament[]> {
        return this.captain
            .isReady()
            .then(() => this.captain.tournaments.getList())
            .catch((error) => {
                console.error(`Failed to get tournaments: ${error.message}`);
                throw error;
            });
    }

    public async getTournamentLeaderboard(params: {
        id: string;
        batchSize: number;
        skip: number;
    }): Promise<{ list: ICULeaderboard[]; total: number }> {
        try {
            await this.captain.isReady();
            const { data: leaderboard, total } = await this.captain.leaderboard.get(
                'tournament',
                false,
                params.batchSize,
                params.skip,
                params.id,
            );

            return {
                list: leaderboard,
                total: total || leaderboard.length,
            };
        } catch (error) {
            console.error(
                `Failed to fetch leaderboard for tournament ${params.id}: ${
                    (error as Error).message
                }`,
            );

            throw error;
        }
    }

    public async getMyTournamentStats(tournamentId: string): Promise<ICULeaderboard[]> {
        return this.captain
            .isReady()
            .then(() => this.captain.leaderboard.tournament(tournamentId))
            .then((leaderboard) => leaderboard.me(2))
            .catch((error) => {
                console.error(
                    `Failed to fetch personal leaderboard for tournament ${tournamentId}: ${error.message}`,
                );
                throw error;
            });
    }
}

export const captainUpAPI = new CaptainUpAPI();
